import React, { useState, useMemo, useEffect } from 'react';
import { X, Users, AlertCircle, CheckCircle, TrendingUp, Award, Activity, RefreshCw, Search } from 'lucide-react';
import { createAssignment } from '../../../models/mmsModel';
import { getWorkerProfile } from '../../../models/externalModel';
import { toast } from 'react-toastify';

/**
 * Bulk Assignment Modal
 * Allows assigning multiple job cards to workers with:
 * - Load balancing suggestions
 * - Worker capacity indicators
 * - Smart distribution algorithms
 * - Progress tracking
 */
const BulkAssignmentModal = ({ jobCards = [], workers = [], onClose, onSuccess }) => {
  const [assignmentStrategy, setAssignmentStrategy] = useState('manual'); // manual, balanced, station
  const [selectedWorker, setSelectedWorker] = useState('');
  const [assignments, setAssignments] = useState({});
  const [notes, setNotes] = useState('');
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [workerProfiles, setWorkerProfiles] = useState({}); // Store worker profiles with skills
  const [loadingProfiles, setLoadingProfiles] = useState(false);
  const [skillsLoaded, setSkillsLoaded] = useState(false);
  const [sortBy, setSortBy] = useState('load'); // load, name, completed, skills
  const [loadingProgress, setLoadingProgress] = useState({ current: 0, total: 0 }); // Track profile loading progress
  const [skillFilter, setSkillFilter] = useState(''); // Filter workers by skill name

  // Log received workers data for debugging
  React.useEffect(() => {
    console.log('BulkAssignmentModal received workers:', workers);
    if (workers.length > 0) {
      console.log('First worker sample:', workers[0]);
      console.log('Worker IDs types:', workers.map(w => ({ 
        id: w.worker_id, 
        type: typeof w.worker_id,
        name: w.worker_name,
        employee_id: w.worker_employee_id 
      })));
    }
  }, [workers]);

  // Don't auto-fetch on mount - wait for user to click "Load Skills"
  const fetchWorkerProfiles = async () => {
    const workersToLoad = workers.filter(w => w.worker_employee_id || w.worker_id);
    const totalWorkers = workersToLoad.length;
    
    setLoadingProfiles(true);
    setSkillsLoaded(false);
    setLoadingProgress({ current: 0, total: totalWorkers });
    setWorkerProfiles({}); // Clear previous profiles
    
    let loadedCount = 0;
    
    for (const worker of workersToLoad) {
      const employeeIdentifier = worker.worker_employee_id || worker.worker_id;
      
      try {
        const response = await getWorkerProfile(employeeIdentifier);
        if (response && response.data && response.data.success) {
          // Update profiles immediately as each one loads
          setWorkerProfiles(prev => ({
            ...prev,
            [worker.worker_id]: response.data
          }));
        }
      } catch (error) {
        console.error(`Failed to load profile for worker ${worker.worker_id}:`, error);
      }
      
      // Update progress after each worker
      loadedCount++;
      setLoadingProgress({ current: loadedCount, total: totalWorkers });
    }
    
    setLoadingProfiles(false);
    setSkillsLoaded(true);
  };

  // Calculate worker load and capacity with sorting and filtering
  const workerStats = useMemo(() => {
    const stats = workers.map(worker => {
      // Count jobs being assigned in this modal to this worker
      const newAssignments = Object.values(assignments).filter(
        assignedWorkerId => assignedWorkerId === worker.worker_id
      ).length;
      
      // Use the actual active_jobs count from the worker data
      const existingJobs = worker.active_jobs || 0;
      const totalLoad = existingJobs + newAssignments;
      
      const profile = workerProfiles[worker.worker_id];
      const completedJobs = profile?.statistics?.completed_jobs || worker.completed_jobs || 0;
      const topSkills = profile?.top_skills || [];
      const topSkillsCount = topSkills.length;
      
      return {
        ...worker,
        existingJobs,
        newAssignments,
        currentLoad: totalLoad,
        completedJobs,
        topSkillsCount,
        topSkills
      };
    });
    
    // Apply skill filter if set
    let filteredStats = stats;
    if (skillFilter.trim()) {
      const filterLower = skillFilter.toLowerCase().trim();
      filteredStats = stats.filter(worker => {
        // Check if any skill matches the filter
        return worker.topSkills.some(skill => 
          skill.ACTIVITY_NAME?.toLowerCase().includes(filterLower) ||
          skill.ACTIVITY_DESCRIPTION?.toLowerCase().includes(filterLower)
        );
      });
    }
    
    // Apply sorting
    switch (sortBy) {
      case 'name':
        return filteredStats.sort((a, b) => a.worker_name.localeCompare(b.worker_name));
      case 'completed':
        return filteredStats.sort((a, b) => b.completedJobs - a.completedJobs);
      case 'skills':
        return filteredStats.sort((a, b) => b.topSkillsCount - a.topSkillsCount);
      case 'load':
      default:
        return filteredStats.sort((a, b) => a.currentLoad - b.currentLoad);
    }
  }, [workers, assignments, workerProfiles, sortBy, skillFilter]);

  // Auto-assign based on strategy
  const autoAssign = (strategy, manualWorkerId = null) => {
    const newAssignments = {};
    
    // Sort workers by existing jobs only (not including new assignments from this modal)
    const sortedWorkers = [...workers]
      .map(w => ({
        ...w,
        existingJobs: w.active_jobs || 0
      }))
      .sort((a, b) => a.existingJobs - b.existingJobs);
    
    switch (strategy) {
      case 'balanced':
        // Distribute evenly across all workers, starting with least loaded
        let workerIndex = 0;
        
        jobCards.forEach((job) => {
          if (sortedWorkers.length > 0) {
            const worker = sortedWorkers[workerIndex % sortedWorkers.length];
            newAssignments[job.JOBCARD_NO] = worker.worker_id;
            workerIndex++;
          }
        });
        break;
        
      case 'station':
        // Assign based on station proximity (if worker has station info)
        jobCards.forEach(job => {
          const matchingWorker = sortedWorkers.find(
            w => w.station_no === job.STATION_NO
          );
          
          if (matchingWorker) {
            newAssignments[job.JOBCARD_NO] = matchingWorker.worker_id;
          } else {
            // Fallback to least loaded worker
            const leastLoaded = sortedWorkers[0]; // Already sorted by load
            if (leastLoaded) {
              newAssignments[job.JOBCARD_NO] = leastLoaded.worker_id;
            }
          }
        });
        break;
        
      case 'manual':
      default:
        // User selects worker manually - use the passed worker ID or the state value
        const workerId = manualWorkerId || selectedWorker;
        if (workerId) {
          jobCards.forEach(job => {
            newAssignments[job.JOBCARD_NO] = workerId;
          });
        }
        break;
    }
    
    setAssignments(newAssignments);
  };

  // Apply assignments
  const handleBulkAssign = async () => {
    const assignmentsList = Object.entries(assignments);
    
    if (assignmentsList.length === 0) {
      toast.error('No assignments to process');
      return;
    }

    console.log('Assignments to process:', assignmentsList);
    setProcessing(true);
    setProgress({ current: 0, total: assignmentsList.length });

    let successCount = 0;
    let failCount = 0;

    for (let i = 0; i < assignmentsList.length; i++) {
      const [jobCardNo, workerId] = assignmentsList[i];
      const job = jobCards.find(j => j.JOBCARD_NO.toString() === jobCardNo);
      
      if (!job) {
        failCount++;
        continue;
      }

      try {
        console.log(`Processing assignment: Job=${jobCardNo}, WorkerId=${workerId} (type: ${typeof workerId})`);
        
        // Backend now handles worker matching - send worker_id as-is
        // It can be numeric worker_id, employee number, or worker name
        await createAssignment({
          jobcard_no: job.JOBCARD_NO,
          application_id: job.APPLICATION_ID,
          property_id: job.PROPERTY_ID || null, // For geocoding the property address
          worker_id: workerId, // Backend will match this to local worker
          notes: notes || `Bulk assignment - ${assignmentStrategy} strategy`
        });
        
        successCount++;
      } catch (err) {
        console.error(`Failed to assign job ${jobCardNo}:`, err);
        
        // Show specific error message from backend
        const errorMessage = err.response?.data?.message || err.message || 'Unknown error';
        toast.error(`Job ${jobCardNo}: ${errorMessage}`);
        
        failCount++;
      }
      
      setProgress({ current: i + 1, total: assignmentsList.length });
    }

    setProcessing(false);

    if (successCount > 0) {
      toast.success(`Successfully assigned ${successCount} job card(s)${failCount > 0 ? `. ${failCount} failed.` : ''}`);
      if (onSuccess) onSuccess();
    } else {
      toast.error('Failed to assign job cards');
    }
  };

  // Manual assignment for individual job
  const handleManualAssignment = (jobCardNo, workerId) => {
    setAssignments(prev => ({
      ...prev,
      [jobCardNo]: workerId
    }));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center bg-gradient-to-r from-purple-50 to-white">
          <div className="flex items-center space-x-3">
            <div className="flex-shrink-0 rounded-lg p-2 bg-purple-100">
              <Users className="h-6 w-6 text-purple-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Bulk Job Assignment</h2>
              <p className="text-sm text-gray-500">Assign {jobCards.length} job card(s) to artisans</p>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={processing}
            className="text-gray-400 hover:text-gray-600 transition-colors p-2 hover:bg-gray-100 rounded-full disabled:opacity-50"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-6">
          {/* Assignment Strategy */}
          <div className="mb-6">
            <h3 className="text-sm font-bold text-gray-800 mb-3 flex items-center">
              <TrendingUp className="h-4 w-4 mr-2 text-purple-600" />
              Assignment Strategy
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <button
                onClick={() => {
                  setAssignmentStrategy('manual');
                  setAssignments({});
                }}
                className={`p-4 border-2 rounded-lg text-left transition-colors ${
                  assignmentStrategy === 'manual'
                    ? 'border-purple-600 bg-purple-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="font-semibold text-gray-900 mb-1">Manual Assignment</div>
                <div className="text-xs text-gray-600">Assign all jobs to a single worker</div>
              </button>

              <button
                onClick={() => {
                  setAssignmentStrategy('balanced');
                  autoAssign('balanced');
                }}
                className={`p-4 border-2 rounded-lg text-left transition-colors ${
                  assignmentStrategy === 'balanced'
                    ? 'border-purple-600 bg-purple-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="font-semibold text-gray-900 mb-1">Balanced Distribution</div>
                <div className="text-xs text-gray-600">Distribute evenly across all workers</div>
              </button>

              <button
                onClick={() => {
                  setAssignmentStrategy('station');
                  autoAssign('station');
                }}
                className={`p-4 border-2 rounded-lg text-left transition-colors ${
                  assignmentStrategy === 'station'
                    ? 'border-purple-600 bg-purple-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="font-semibold text-gray-900 mb-1">Station-Based</div>
                <div className="text-xs text-gray-600">Match workers to their stations</div>
              </button>
            </div>
          </div>

          {/* Manual Selection Instruction */}
          {assignmentStrategy === 'manual' && (
            <div className="mb-4">
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                <p className="text-sm text-purple-900">
                  <span className="font-semibold">Click on a worker card below</span> to assign the selected job(s) to that worker.
                </p>
              </div>
            </div>
          )}

          {/* Worker Load Overview with Skills */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-bold text-gray-800 flex items-center">
                <Activity className="h-4 w-4 mr-2 text-purple-600" />
                Worker Capacity & Skills
              </h3>
              
              <div className="flex items-center space-x-3">
                {/* Sort Dropdown */}
                {skillsLoaded && (
                  <div className="flex items-center space-x-2">
                    <label className="text-xs text-gray-600">Sort by:</label>
                    <select
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                      className="text-xs px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-purple-500"
                    >
                      <option value="load">Current Load</option>
                      <option value="name">Name</option>
                      <option value="completed">Completed Jobs</option>
                      <option value="skills">Skills Count</option>
                    </select>
                  </div>
                )}
                
                {/* Load Skills Button */}
                {!skillsLoaded && !loadingProfiles && (
                  <button
                    onClick={fetchWorkerProfiles}
                    className="flex items-center space-x-2 px-3 py-1.5 bg-purple-600 text-white text-xs font-medium rounded-lg hover:bg-purple-700 transition-colors"
                  >
                    <Award className="h-3 w-3" />
                    <span>Load Skills & Performance</span>
                  </button>
                )}
                
                {/* Refresh Button */}
                {skillsLoaded && !loadingProfiles && (
                  <button
                    onClick={fetchWorkerProfiles}
                    className="flex items-center space-x-1 px-2 py-1 text-xs text-purple-600 hover:bg-purple-50 rounded transition-colors"
                    title="Refresh skills data"
                  >
                    <RefreshCw className="h-3 w-3" />
                    <span>Refresh</span>
                  </button>
                )}
              </div>
            </div>
            
            {/* Skill Filter - Shows when skills are loaded */}
            {skillsLoaded && !loadingProfiles && (
              <div className="mb-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Filter by skill (e.g., REIN, INST, MECH)..."
                    value={skillFilter}
                    onChange={(e) => setSkillFilter(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                  {skillFilter && (
                    <button
                      onClick={() => setSkillFilter('')}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      title="Clear filter"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
                {skillFilter && (
                  <div className="mt-2 text-xs text-gray-600">
                    {workerStats.length === 0 ? (
                      <span className="text-amber-600">No workers found with skills matching "{skillFilter}"</span>
                    ) : workerStats.length === workers.length ? (
                      <span>Showing all {workers.length} workers</span>
                    ) : (
                      <span>Showing {workerStats.length} of {workers.length} workers with matching skills</span>
                    )}
                  </div>
                )}
              </div>
            )}
            
            {/* Progress Bar - Shows when loading profiles */}
            {loadingProfiles && (
              <div className="mb-4 bg-purple-50 border border-purple-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-purple-900">
                    Loading worker profiles...
                  </span>
                  <span className="text-sm font-semibold text-purple-700">
                    {loadingProgress.current} / {loadingProgress.total}
                  </span>
                </div>
                
                {/* Progress Bar */}
                <div className="w-full bg-purple-200 rounded-full h-2.5 overflow-hidden">
                  <div 
                    className="bg-purple-600 h-2.5 rounded-full transition-all duration-300 ease-out"
                    style={{ 
                      width: `${loadingProgress.total > 0 ? (loadingProgress.current / loadingProgress.total) * 100 : 0}%` 
                    }}
                  ></div>
                </div>
                
                {/* Percentage */}
                <div className="mt-1 text-xs text-purple-600 text-right">
                  {loadingProgress.total > 0 
                    ? Math.round((loadingProgress.current / loadingProgress.total) * 100) 
                    : 0}% complete
                </div>
              </div>
            )}
            
            {/* Empty State - No workers match filter */}
            {skillsLoaded && skillFilter && workerStats.length === 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 text-center">
                <Award className="h-8 w-8 text-amber-400 mx-auto mb-2" />
                <p className="text-sm font-medium text-amber-900 mb-1">No workers found</p>
                <p className="text-xs text-amber-700">
                  No workers have skills matching "{skillFilter}". Try a different search term or{' '}
                  <button 
                    onClick={() => setSkillFilter('')}
                    className="underline hover:text-amber-900 font-medium"
                  >
                    clear the filter
                  </button>.
                </p>
              </div>
            )}
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4">
              {workerStats.map(worker => {
                const profile = workerProfiles[worker.worker_id];
                const topSkills = profile?.top_skills || [];
                const stats = profile?.statistics || {};
                
                console.log(`Rendering worker card for: ${worker.worker_id}`, {
                  worker,
                  profile,
                  hasProfile: !!profile,
                  topSkillsCount: topSkills.length,
                  allProfiles: Object.keys(workerProfiles)
                });
                
                const isSelected = assignmentStrategy === 'manual' && selectedWorker === worker.worker_id;
                
                return (
                  <div 
                    key={worker.worker_id} 
                    onClick={() => {
                      if (assignmentStrategy === 'manual') {
                        setSelectedWorker(worker.worker_id);
                        autoAssign('manual', worker.worker_id);
                      }
                    }}
                    className={`bg-white border-2 rounded-lg p-4 transition-all ${
                      assignmentStrategy === 'manual' ? 'cursor-pointer' : ''
                    } ${
                      isSelected 
                        ? 'border-purple-600 bg-purple-50 shadow-md' 
                        : 'border-gray-200 hover:border-purple-300'
                    }`}
                  >
                    {/* Worker Header */}
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex-1">
                        <div className="flex items-center">
                          <span className="text-sm font-semibold text-gray-900 block">{worker.worker_name}</span>
                          {isSelected && (
                            <CheckCircle className="h-4 w-4 ml-2 text-purple-600" />
                          )}
                        </div>
                        {profile?.profile?.POSITION_NAME && (
                          <span className="text-xs text-gray-500">{profile.profile.POSITION_NAME}</span>
                        )}
                      </div>
                      <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-blue-100 text-blue-800">
                        {worker.currentLoad} jobs
                      </span>
                    </div>

                    {/* Current Load */}
                    <div className="flex items-center text-xs text-gray-600 mb-3 space-x-3">
                      <div>
                        <span className="font-medium text-gray-700">Active:</span> {worker.existingJobs}
                      </div>
                      {worker.newAssignments > 0 && (
                        <div className="text-purple-600 font-medium">
                          +{worker.newAssignments} new
                        </div>
                      )}
                      {stats.completed_jobs > 0 && (
                        <div>
                          <span className="font-medium text-gray-700">Completed:</span> {stats.completed_jobs}
                        </div>
                      )}
                    </div>

                    {/* Top Skills */}
                    {topSkills.length > 0 && (
                      <div className="border-t border-gray-100 pt-3">
                        <div className="flex items-center mb-2">
                          <Award className="h-3 w-3 text-amber-500 mr-1" />
                          <span className="text-xs font-semibold text-gray-700">Top Skills</span>
                        </div>
                        <div className="space-y-1.5">
                          {topSkills.slice(0, 3).map((skill, idx) => (
                            <div key={idx} className="flex items-center justify-between text-xs">
                              <div className="flex-1 min-w-0 mr-2">
                                <div className="font-medium text-gray-700 truncate">
                                  {skill.ACTIVITY_NAME || 'Unknown'}
                                </div>
                                <div className="text-gray-500 text-[10px] truncate">
                                  {skill.ACTIVITY_DESCRIPTION}
                                </div>
                              </div>
                              <div className="flex items-center space-x-1.5 flex-shrink-0">
                                <span className="text-gray-600">{skill.jobs_handled} jobs</span>
                                <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                                  parseFloat(skill.completion_rate) >= 50 
                                    ? 'bg-green-100 text-green-700'
                                    : parseFloat(skill.completion_rate) >= 25
                                    ? 'bg-yellow-100 text-yellow-700'
                                    : 'bg-red-100 text-red-700'
                                }`}>
                                  {parseFloat(skill.completion_rate).toFixed(0)}%
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* No skills message for internal users */}
                    {profile?.is_internal_user && topSkills.length === 0 && (
                      <div className="border-t border-gray-100 pt-3">
                        <div className="text-xs text-gray-500 italic">
                          Internal user - No field job history
                        </div>
                      </div>
                    )}

                    {/* Waiting for profile - show placeholder instead of spinner */}
                    {!profile && loadingProfiles && (
                      <div className="border-t border-gray-100 pt-3">
                        <div className="text-xs text-gray-400 italic">
                          Loading profile data...
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Assignment Notes */}
          <div className="mb-6">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Assignment Notes (Optional)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows="3"
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
              placeholder="Add notes for these assignments..."
              disabled={processing}
            />
          </div>

          {/* Job Cards to Assign */}
          <div>
            <h3 className="text-sm font-bold text-gray-800 mb-3">Job Cards ({jobCards.length})</h3>
            <div className="bg-gray-50 rounded-lg border border-gray-200 max-h-64 overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-100 sticky top-0">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Job Card</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Description</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Assigned Worker</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {jobCards.map(job => {
                    const assignedWorkerId = assignments[job.JOBCARD_NO];
                    const assignedWorker = workers.find(w => w.worker_id === assignedWorkerId);
                    
                    return (
                      <tr key={job.JOBCARD_NO} className="hover:bg-white">
                        <td className="px-3 py-2 font-medium text-gray-900">#{job.JOBCARD_NO}</td>
                        <td className="px-3 py-2 text-gray-700 truncate max-w-xs">
                          {job.JOB_DESCRIPTION || job.APPLICATION_DESCRIPTION || 'No description'}
                        </td>
                        <td className="px-3 py-2">
                          {assignedWorker ? (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              <CheckCircle className="h-3 w-3 mr-1" />
                              {assignedWorker.worker_name}
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                              <AlertCircle className="h-3 w-3 mr-1" />
                              Not assigned
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Progress Bar */}
          {processing && (
            <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-blue-900">Processing assignments...</span>
                <span className="text-sm text-blue-700">{progress.current} / {progress.total}</span>
              </div>
              <div className="w-full bg-blue-200 rounded-full h-2">
                <div
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${(progress.current / progress.total) * 100}%` }}
                ></div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-between items-center">
          <div className="text-sm text-gray-600">
            {Object.keys(assignments).length} of {jobCards.length} jobs assigned
          </div>
          <div className="flex space-x-3">
            <button
              onClick={onClose}
              disabled={processing}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors font-medium disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleBulkAssign}
              disabled={processing || Object.keys(assignments).length === 0}
              className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium disabled:opacity-50 flex items-center"
            >
              {processing ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Processing...
                </>
              ) : (
                <>
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Assign {Object.keys(assignments).length} Job(s)
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BulkAssignmentModal;
